/* eslint-disable max-lines-per-function */
import { ComboboxContext } from '@/components/Combobox/context/ComboboxContext';
import {
  ComboboxContextProviderProps,
  ComboboxContextValue,
  ComboboxOptionBase,
} from '@/components/Combobox/types';
import { useState } from 'react';

const normalizeValues = (values: string[]) => Array.from(new Set(values));

export function ComboboxContextProvider<T extends ComboboxOptionBase>({
  children,
  options,
  values,
  defaultValues = [],
  onValuesChange,
  disabled = false,
  showClear = true,
  placeholder,
  maxShownChips,
  customChipContent,
}: ComboboxContextProviderProps<T>) {
  const [internalValues, setInternalValues] = useState<string[]>(defaultValues);
  const [filterValues, setFilterValues] = useState<string[]>([]);

  const terms = filterValues.map((t) => t.trim().toLowerCase());
  const normalize = (v: unknown) => String(v ?? '').toLowerCase();

  const filteredOptions = options.filter((option) => {
    if (filterValues.length === 0) {
      return true;
    }

    return terms.some((term) =>
      Object.values(option).some((val) => normalize(val).includes(term)),
    );
  });

  const selectedValues = values ?? internalValues;
  const selectedValueSet = new Set(selectedValues);
  const selectedOptions = options.filter((option) =>
    selectedValueSet.has(option.value),
  );

  const setSelectedValues = (nextValues: string[]) => {
    const normalized = normalizeValues(nextValues);

    if (values === undefined) {
      setInternalValues(normalized);
    }

    const nextSet = new Set(normalized);
    const nextSelectedOptions = options.filter((option) =>
      nextSet.has(option.value),
    );
    onValuesChange?.(normalized, nextSelectedOptions);
  };

  const toggleByValue = (targetValue: string) => {
    if (selectedValueSet.has(targetValue)) {
      setSelectedValues(
        selectedValues.filter((valueItem) => valueItem !== targetValue),
      );
      return;
    }

    setSelectedValues([...selectedValues, targetValue]);
  };

  const clear = () => {
    setSelectedValues([]);
  };

  const removeValueFromSelectedOptions = (value: string) => {
    setSelectedValues(selectedValues.filter((v) => v !== value));
  };

  const toggleFilter = (targetValue: string) => {
    if (filterValues.includes(targetValue)) {
      setFilterValues(filterValues.filter((value) => value !== targetValue));
      return;
    }

    setFilterValues([...filterValues, targetValue]);
  };

  const contextValue: ComboboxContextValue<T> = {
    clear,
    options: filteredOptions,
    selectedValues,
    selectedOptions,
    isSelected: (value) => selectedValueSet.has(value),
    toggleByValue,
    setSelectedValues,
    removeValueFromSelectedOptions,
    customChipContent,
    disabled,
    showClear,
    placeholder,
    maxShownChips,
    filterValues,
    toggleFilter,
  };

  return (
    <ComboboxContext.Provider
      value={
        contextValue as unknown as ComboboxContextValue<ComboboxOptionBase>
      }>
      {children}
    </ComboboxContext.Provider>
  );
}
